import { formatUnits } from '@ethersproject/units';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Countdown, Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { getNetworkLogo } from '@/common/networkUtils';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import { InstanceState } from '@/common/util';
import StyledButton from '@/components/StyledButton';
import { useClaim, useInstanceStatus } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useReservoirFloorToken } from '@/hooks/useReservoirFloorToken';
import useMainStore from '@/store/mainStore';

type CallToActionUXProps = {
  onAction: () => Promise<void>;
  onSecondaryAction: () => Promise<void>;
  className?: string;
  variant?: 'desktop' | 'mobile'; // just changes the UX and layout of the button
};

export default function CallToActionUX({
  onAction,
  onSecondaryAction,
  className = '',
  variant = 'desktop',
}: CallToActionUXProps) {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const instanceStatus = useInstanceStatus();
  const { claim } = useClaim();
  const [countdownText, setCountdownText] = useState<string | null>(null);
  const [mintPrice, setMintPrice] = useState<string | undefined>('');
  const analytics = useAnalytics();

  // use hook to ensure we are showing the latest floor token data
  const { floorToken: reservoirFloorToken, isLoading: reservoirFloorTokenIsLoading } =
    useReservoirFloorToken();

  const showGatedBadge =
    (studioInstance?.publicData.mintingRestriction === 'Allowlist' ||
      studioInstance?.publicData.mintingRestriction === 'RedemptionCodes') &&
    // @dev dont show when people are buying on secondary bc it's no longer relevant
    (instanceStatus === InstanceState.Live || instanceStatus === InstanceState.Upcoming);

  const getFormattedStartDate = (startDate: number) => {
    return new Date(startDate).toLocaleString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  // Update countdown text every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (studioInstance && claim) {
        if (instanceStatus === InstanceState.Live && claim?.endDate) {
          setCountdownText(`Ends in ${Countdown.getCountdownFromTimestamp(claim.endDate)}`);
        } else if (instanceStatus === InstanceState.Upcoming && claim?.startDate) {
          setCountdownText(`Starts in ${Countdown.getCountdownFromTimestamp(claim.startDate)}`);
        } else if (instanceStatus === InstanceState.SoldOut) {
          setCountdownText('Sold Out');
        } else if (instanceStatus === InstanceState.Ended) {
          if (claim.endDate) {
            setCountdownText(
              `Claim ended on ${new Date(claim.endDate).toLocaleString('en-US', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}`,
            );
          } else {
            setCountdownText('Ended');
          }
        } else {
          setCountdownText(null);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [claim, instanceStatus, studioInstance]);

  const getButtonText = () => {
    switch (instanceStatus) {
      case InstanceState.Loading:
        return 'Loading...';
      case InstanceState.Ended:
        return 'Ended';
      case InstanceState.Upcoming:
        return `Drops ${getFormattedStartDate(studioInstance?.publicData.startDate || 0)}`;
      case InstanceState.SoldOut:
        return 'Sold Out';
      case InstanceState.Live: {
        return 'Mint';
      }
      // error state
      default:
        return 'Contact Support';
    }
  };

  useEffect(() => {
    if (!studioInstance) return;
    if (!claim) return;

    // @dev: instanceStatus is dependent on both claim and studioInstance
    if (instanceStatus === InstanceState.Live || instanceStatus === InstanceState.Upcoming) {
      if (studioInstance?.publicData.mintPrice) {
        if (studioInstance?.publicData.mintPrice.value === '0') {
          setMintPrice('Free');
        } else {
          const newMintPrice =
            formatUnits(
              studioInstance?.publicData.mintPrice.value,
              studioInstance?.publicData.mintPrice.decimals,
            ) +
            ' ' +
            studioInstance?.publicData.mintPrice.symbol;

          setMintPrice(newMintPrice);
        }
      }
    } else if (reservoirFloorToken) {
      // get the floor price from reservoir
      setMintPrice(
        reservoirFloorToken.market.floorAsk.price.amount.decimal +
          ' ' +
          reservoirFloorToken.market.floorAsk.price.currency.symbol,
      );
    } else {
      // show nothing
    }
  }, [studioInstance, claim, instanceStatus, reservoirFloorToken, variant]);

  // can only buy on secondary if the sale is over AND there is a secondary token id to be bought
  const potentialToBeBoughtOnSecondary =
    instanceStatus === InstanceState.SoldOut || instanceStatus === InstanceState.Ended;

  // Gated badge component for reuse
  const GatedBadge = () => (
    <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-md m-claim--bg-surface-warning m-claim--px-3 m-claim--py-1.5 m-claim--text-xs m-claim--font-semibold m-claim--text-primary">
      Gated
    </div>
  );

  // Network indicator component for reuse
  const NetworkIndicator = ({ compact = false }: { compact?: boolean }) => {
    if (!studioInstance?.publicData.network) return null;

    const networkId = studioInstance.publicData.network;
    const networkName = Network.getDisplayNameForNetworkId(networkId);
    const networkLogo = getNetworkLogo(networkId);

    if (compact) {
      return (
        <div className="m-claim--flex m-claim--items-center m-claim--gap-1.5 m-claim--rounded-md m-claim--bg-surface-accent m-claim--px-3 m-claim--py-1.5">
          <img
            src={networkLogo}
            alt={networkName}
            className="m-claim--size-3.5"
          />
          <span className="m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
            {StringUtils.capitalizeFirstLetter(networkName)}
          </span>
        </div>
      );
    }

    return (
      <div
        className={`m-claim--flex m-claim--items-center m-claim--gap-1.5 m-claim--rounded-md m-claim--bg-surface-accent m-claim--px-2 m-claim--py-1.5 ${className}`}
      >
        <img
          src={networkLogo}
          alt={networkName}
          className="m-claim--size-3"
        />
        <span className="m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
          {StringUtils.capitalizeFirstLetter(networkName)}
        </span>
      </div>
    );
  };

  const handleActionButtonClick = () => {
    analytics.capture({
      event: TELEMETRY_EVENT_MAP.CHECKOUT_PREVIEW_CLICKED,
    });
    onAction();
  };

  const handleSecondaryActionButtonClick = () => {
    analytics.capture({
      event: TELEMETRY_EVENT_MAP.SECONDARY_CHECKOUT_PREVIEW_CLICKED,
    });
    onSecondaryAction();
  };

  // Render the mobile version
  if (variant === 'mobile') {
    return (
      // eslint-disable-next-line tailwindcss/no-custom-classname
      <div className="m-claim--mobile-action-button m-claim--w-full m-claim--rounded-t-md m-claim--border-t m-claim--border-default m-claim--bg-surface-secondary m-claim--shadow-lg">
        {!studioInstance || !claim || instanceStatus === InstanceState.Loading ? (
          <Skeleton height={80} />
        ) : (
          <div className="m-claim--px-5 m-claim--py-3">
            <div className="m-claim--flex m-claim--flex-col m-claim--gap-3">
              {mintPrice && (
                <div className="m-claim--flex m-claim--flex-col m-claim--gap-2">
                  <div className="m-claim--flex m-claim--flex-row m-claim--justify-between m-claim--gap-2">
                    <div className="m-claim--text-lg m-claim--font-bold">{mintPrice}</div>
                    <div className="m-claim--flex m-claim--flex-row m-claim--gap-2">
                      <NetworkIndicator compact />
                      {showGatedBadge && <GatedBadge />}
                    </div>
                  </div>
                  <div className="m-claim--flex m-claim--flex-wrap m-claim--items-center m-claim--gap-2">
                    <span className="m-claim--text-xs m-claim--text-muted">+ MANIFOLD FEE</span>
                  </div>
                </div>
              )}
              {instanceStatus === InstanceState.Live && (
                <StyledButton
                  version="primary"
                  className="m-claim--w-full m-claim--px-4 m-claim--py-2.5"
                  onClick={handleActionButtonClick}
                >
                  {getButtonText()}
                </StyledButton>
              )}

              {potentialToBeBoughtOnSecondary && (
                <StyledButton
                  version="primary"
                  className="m-claim--w-full m-claim--px-4 m-claim--py-2.5"
                  onClick={handleSecondaryActionButtonClick}
                  disabled={!reservoirFloorToken || reservoirFloorTokenIsLoading}
                >
                  {reservoirFloorToken ? 'Buy Listing' : 'No listings for sale'}
                </StyledButton>
              )}
            </div>

            {/* Using a styling trick to escape the padding on either side and appear full width */}
            <hr className="m-claim--my-3 m-claim--w-[calc(100%+2rem)] m-claim--translate-x-[-1rem]" />

            {/* Compact Progress Bar for Mobile */}
            {claim.totalMax && claim.totalMax > 0 && (
              <div className="m-claim--mt-3">
                <div className="m-claim--mb-1 m-claim--flex m-claim--justify-between m-claim--text-xs">
                  <span className="m-claim--font-medium">{claim.total} minted</span>
                  <span className="m-claim--font-medium m-claim--text-warning">
                    {claim.totalMax - claim.total} remaining
                  </span>
                </div>
                <div className="m-claim--h-1 m-claim--w-full m-claim--rounded-full m-claim--bg-surface-accent">
                  <div
                    className="m-claim--h-full m-claim--rounded-full m-claim--bg-surface-contrast"
                    style={{ width: `${Math.min(100, (claim.total / claim.totalMax) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {!claim.totalMax && (
              <div className="m-claim--mt-2 m-claim--flex m-claim--justify-between m-claim--text-sm">
                <span className="m-claim--font-semibold m-claim--text-warning">
                  {claim.total} minted
                </span>
                <span className="m-claim--font-medium m-claim--text-secondary">Open edition</span>
              </div>
            )}

            {countdownText && (
              <>
                <hr className="m-claim--my-3 m-claim--w-[calc(100%+2rem)] m-claim--translate-x-[-1rem]" />

                <div className="m-claim--text-center m-claim--font-semimono m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
                  {countdownText}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Render the desktop version (default)
  return (
    <div
      className={`m-claim--w-full m-claim--overflow-hidden m-claim--rounded-md m-claim--border m-claim--border-default ${className} m-claim--bg-surface-primary`}
    >
      {!studioInstance || !claim || instanceStatus === InstanceState.Loading ? (
        <Skeleton height={150} />
      ) : (
        <>
          <div className="m-claim--p-5">
            {/* Mint Price Display */}
            {mintPrice && (
              <div className="m-claim--mb-6 m-claim--flex m-claim--flex-col m-claim--gap-2">
                <div className="m-claim--flex m-claim--items-center m-claim--justify-between m-claim--gap-2">
                  <div className="m-claim--text-lg m-claim--font-bold">{mintPrice}</div>
                  <div className="m-claim--flex m-claim--flex-row m-claim--gap-2">
                    {showGatedBadge && <GatedBadge />}
                    <NetworkIndicator />
                  </div>
                </div>
                <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-xs m-claim--text-muted">
                  <span>+ MANIFOLD FEE</span>
                </div>
              </div>
            )}

            {/* Mint Button */}
            {instanceStatus === InstanceState.Live && (
              <StyledButton
                version="primary"
                className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--gap-2 m-claim--py-3 m-claim--text-center"
                onClick={handleActionButtonClick}
              >
                {getButtonText()}
              </StyledButton>
            )}

            {potentialToBeBoughtOnSecondary && (
              <StyledButton
                version="primary"
                className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--gap-2 m-claim--py-3 m-claim--text-center"
                onClick={handleSecondaryActionButtonClick}
                disabled={!reservoirFloorToken || reservoirFloorTokenIsLoading}
              >
                {reservoirFloorTokenIsLoading
                  ? 'Loading...'
                  : reservoirFloorToken
                    ? 'Buy Listing'
                    : 'No listings for sale'}
              </StyledButton>
            )}
          </div>

          <hr />

          {/* Progress Bar Section */}
          <div className="m-claim--p-0">
            {!claim.totalMax ? (
              <div className="m-claim--flex m-claim--items-center m-claim--justify-between m-claim--px-4 m-claim--py-3 m-claim--text-sm">
                <span className="m-claim--font-bold m-claim--text-warning">
                  {claim.total} minted
                </span>
                <span className="m-claim--flex m-claim--items-center m-claim--text-secondary">
                  <span className="m-claim--font-medium">Open edition</span>
                </span>
              </div>
            ) : (
              claim.totalMax &&
              claim.totalMax > 0 && (
                <div className="m-claim--p-5">
                  <div className="m-claim--flex m-claim--justify-between m-claim--pb-1 m-claim--text-sm">
                    <span className="m-claim--font-medium">{claim.total} minted</span>
                    <span className="m-claim--font-medium m-claim--text-warning">
                      {claim.totalMax - claim.total} remaining
                    </span>
                  </div>
                  <div className="m-claim--h-2 m-claim--w-full m-claim--bg-surface-accent">
                    <div
                      className="m-claim--h-full m-claim--bg-surface-contrast"
                      style={{ width: `${Math.min(100, (claim.total / claim.totalMax) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              )
            )}
          </div>

          {countdownText && (
            <>
              <hr />
              <div className=" m-claim--px-4 m-claim--py-2 m-claim--text-center m-claim--font-semimono m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
                {countdownText}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
